/**
 * Mass DM versturen — methodekeuze, filters, vertaler, preview en planning.
 *
 * 16-09-2026 (CHAT-013) stond hier een klein venster met model, platform,
 * methode, publiek en tekst. 18-09-2026 (Jordy, HERSTEL-008) bleek dat het
 * naast het oude dashboard nog het meeste miste. Het oude venster
 * (`admin-dashboard/src/pages/conversations/MassDmDialog.tsx`) heeft:
 *
 *   - een KEUZE VOORAF tussen Platform Mass DM en Dashboard Mass Message,
 *     met "Change method" in de titelbalk om terug te gaan;
 *   - één modellijst (met platform erbij) in plaats van losse knoppen;
 *   - een vertaalschakelaar: je typt Engels, de fan krijgt zijn eigen taal;
 *   - een doelgroepkeuze ("Send to") en vijf uitsluitingen plus "online only";
 *   - een verplichte preview vóór verzenden.
 *
 * Dat staat nu allemaal hier. Wat bewust NIET in deze ronde zit: bijlagen uit
 * de bibliotheek, betaalde mass-PPV en "schrijf de tekst op de foto". Die gaan
 * bij de server langs een ander pad (`MassMessageRequest` kent geen
 * bijlagevelden), dus dat is een eigen stap — zie het dossier.
 *
 * Plannen gaat naar `mass-message/schedule`; de server heeft daar al een
 * volledige afhandeling voor (achtergrondlus + overzicht + annuleren).
 *
 * 03-10-2026 (Jordy): de velden, de vertaling vooraf en de regels (minimumprijs,
 * tekst verplicht bij F2F Platform Mass DM) staan nu in `MassDmFormulier.tsx`,
 * zodat de tab "Create" op Mass Messages precies hetzelfde formulier gebruikt.
 * Hier blijven de methodekeuze, de knoppen, de preview en het plannen.
 *
 * 07-10-2026 (Jordy): plannen in de tijdzone van de gebruiker (Max en Rosie:
 * Asia/Manila) met de gedeelde TijdKiezer. Vroeger las het datetime-veld de
 * tijdzone van de browser; nu de voorkeur (zonder voorkeur Amsterdam). De
 * server krijgt het absolute moment in UTC.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MIN_VOORUIT_MS, standaardNieuwMoment, TIJD_VOORBIJ } from '../lib/tijdkeuze'
import { metZoneEnAmsterdam, zetOm, zoneLabel, zoneNaarUtc } from '../lib/amsterdam'
import { useBijZoneWissel, useMijnZone } from '../lib/tijdzone'
import { TijdKiezer } from './TijdKiezer'
import { Modal, Field } from './Modal'
import { useActions } from '../lib/actions'
import { api } from '../lib/api'
import type { Persona } from '../pages/Models'
import {
  MassDmBerichtVoorbeeld,
  MassDmTelling,
  MassDmVelden,
  leegMassDm,
  massDmAfgeleid,
  massDmFouten,
  massVerzoek as massDmVerzoek,
  mediaFormulier,
  modelKeuzes,
  type MassDmWaarden,
  type MassPreview,
  type Methode,
  type ZetMassDm,
} from './MassDmFormulier'

export function MassDmSendDialog({
  personas,
  platformFilter,
  onClose,
  magDirect = true,
  magNative = true,
  magBetaald = true,
}: {
  personas: Persona[]
  /** Het platformfilter van de pagina: 'all' | 'fancentro' | 'f2f'. */
  platformFilter: string
  onClose: () => void
  /** 30-09-2026 (Jordy): alleen de methodes die dit account mag. De server
   *  weigert de andere ook; zo staat er geen knop die niets doet. */
  magDirect?: boolean
  magNative?: boolean
  /** Mag er een prijs aan (PPV)? Anders alleen gratis content. */
  magBetaald?: boolean
}) {
  const { run, busy } = useActions()
  const keuzes = useMemo(() => modelKeuzes(personas), [personas])

  // Stap 1: welke methode. Zolang dit leeg is, staat het keuzescherm er.
  // 05-10-2026 (Jordy): Platform Mass DM is voor iedereen de standaard; wie
  // ook Dashboard Mass Message mag, wisselt met "Change method".
  const enigeMethode: Methode | null = magNative ? 'native' : magDirect ? 'direct' : null
  // 03-10-2026: de velden als één waarde (zie MassDmFormulier.tsx).
  const [w, setW] = useState<MassDmWaarden>(() => leegMassDm(enigeMethode))
  const zet = useCallback<ZetMassDm>((patch) => setW((oud) => ({ ...oud, ...patch })), [])
  const methode = w.methode
  const setMethode = (m: Methode | null) => zet({ methode: m })
  const [preview, setPreview] = useState<MassPreview | null>(null)
  const [bevestiging, setBevestiging] = useState('')
  // 06-10-2026 (Jordy): standaard nu + 15 minuten (lib/tijdkeuze.ts).
  const zone = useMijnZone()
  const [plan, setPlan] = useState(() => standaardNieuwMoment(undefined, zone))
  useBijZoneWissel(zone, (van, naar) => setPlan((m) => zetOm(m, van, naar)))
  // Het absolute moment (UTC) van datum en tijd in `zone`; null als het niet klopt.
  const planMoment = zoneNaarUtc(plan.datum, plan.tijd, zone)
  // 18-09-2026: naast de telling ook zien hóe het bericht aankomt —
  // hetzelfde venster als bij de bijlagenkiezer, zodat er één regel is.
  const [layoutOpen, setLayoutOpen] = useState(false)
  const previewPlek = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (layoutOpen) previewPlek.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [layoutOpen])

  const a = massDmAfgeleid(w, keuzes, magBetaald)
  const { gekozen, naarEchteFans, metMedia, overschrijf, basis } = a
  const tekst = w.tekst

  /** Elke wijziging maakt een eerdere preview ongeldig — anders verstuur je
   *  iets anders dan wat je hebt gezien. Een (aangepaste) vertaling verandert
   *  het aantal niet; die vraagt alleen de bevestiging opnieuw. */
  const wijzig = (soort: 'alles' | 'bevestiging') => {
    if (soort === 'alles') setPreview(null)
    setBevestiging('')
  }

  function massVerzoek<T>(dryRun: boolean): Promise<T> {
    return massDmVerzoek<T>(w, a, dryRun)
  }
  function plannen(): Promise<unknown> {
    const moment = planMoment as string
    // 03-10-2026: een geplande Mass DM wordt pas later verstuurd. Met een
    // bekeken vertaling plannen we die Nederlandse tekst zelf in, met de
    // vertaler uit, zodat er later precies dit uitgaat.
    if (overschrijf) {
      return metMedia
        ? api.postForm('/api/admin/conversations/mass-media/schedule',
                       mediaFormulier(w, a, { scheduled_at: moment, caption: overschrijf,
                                              translation_enabled: 'false', translate_to: '',
                                              delivery_text_override: '' }))
        : api.post('/api/admin/conversations/mass-message/schedule', {
          ...basis, content: overschrijf, translation_enabled: false,
          translate_to: undefined, delivery_text_override: undefined, scheduled_at: moment,
        })
    }
    return metMedia
      ? api.postForm('/api/admin/conversations/mass-media/schedule',
                     mediaFormulier(w, a, { scheduled_at: moment }))
      : api.post('/api/admin/conversations/mass-message/schedule', { ...basis, scheduled_at: moment })
  }
  const naVerzenden = () => {
    onClose()
    zet({ tekst: '', media: null, vertaling: null, aangepast: false })
  }

  const kanPreviewen = Boolean(gekozen) && busy !== 'Mass DM preview'
  // 01-10-2026 (Jordy): een preview is niet meer verplicht vóór versturen of
  // plannen. Preview count en Preview message blijven, als losse knoppen. De
  // getypte SEND bij echte fans blijft wél. Wat er verder moet kloppen (model,
  // tekst, minimumprijs, vertaling) staat in `massDmFouten`.
  const klaarVoorActie =
    massDmFouten(w, a).length === 0
    && (!naarEchteFans || bevestiging.trim().toUpperCase() === 'SEND')
  const kanVersturen = klaarVoorActie && busy !== 'Mass DM'
  // 06-10-2026: een tijd die voorbij is (of binnen 2 minuten) plant niets in.
  const planMs = planMoment ? Date.parse(planMoment) : NaN
  const planVoorbij = Boolean(plan.datum && plan.tijd) && (!Number.isFinite(planMs) || planMs < Date.now() + MIN_VOORUIT_MS)
  const kanPlannen = klaarVoorActie && Boolean(planMoment) && !planVoorbij && busy !== 'Mass DM plannen'

  // ── Stap 1: de methodekeuze ─────────────────────────────────────
  if (methode === null) {
    return (
      <Modal
        title="Mass DM"
        desc="Choose how this message goes out. You can change this later."
        width={620}
        onClose={onClose}
        footer={<button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>}
      >
        {magDirect ? (
        <button className="methodekeuze" type="button" onClick={() => setMethode('direct')}>
          <b>Dashboard Mass Message</b>
          <span>
            One message per chat from the dashboard, with the agency filters
            (audience, exclusions, online only) applied.
          </span>
        </button>
        ) : null}
        {magNative ? (
        <button className="methodekeuze" type="button" onClick={() => setMethode('native')}>
          <b>Platform Mass DM</b>
          <span>
            One campaign in the platform’s own Mass DM. FanCentro keeps its limit
            of 6 campaigns per 24 hours; dashboard filters do not apply.
          </span>
        </button>
        ) : null}
      </Modal>
    )
  }

  const titel = methode === 'native' ? 'Platform Mass DM' : 'Dashboard Mass Message'

  return (
    <Modal
      title={titel}
      desc={
        <>
          Preview first — nothing is sent until you press Send.{' '}
          {/* 18-09-2026 (Jordy): vanuit Dashboard Mass Message moest je terug
              kunnen naar Platform Mass DM. Deze knop zet de keuze terug en
              gooit de preview weg, zodat je nooit met een preview van de
              andere methode verstuurt. 30-09: alleen als er iets te kiezen
              valt. */}
          {magDirect && magNative ? (
          <button
            className="linkbtn"
            type="button"
            onClick={() => { setPreview(null); setBevestiging(''); setMethode(null) }}
          >
            Change method
          </button>
          ) : null}
        </>
      }
      width={640}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn sm"
            type="button"
            disabled={!kanPreviewen}
            onClick={() => run({
              label: 'Mass DM preview',
              // 30-09-2026: via de worker, zie lib/massDmWorker.ts.
              request: () => massVerzoek<MassPreview>(true)
                .then((p) => { setPreview(p); return p }),
              successText: 'Preview only — nothing was sent.',
            })}
          >
            Preview count
          </button>
          <button
            className="btn sm"
            type="button"
            disabled={!tekst.trim() && !metMedia}
            onClick={() => setLayoutOpen((v) => !v)}
          >
            {layoutOpen ? 'Hide preview' : 'Preview message'}
          </button>
          <button
            className="btn sm"
            type="button"
            disabled={!kanPlannen}
            title={planMoment ? undefined : 'Pick a date and time first'}
            onClick={() => run({
              label: 'Mass DM plannen',
              confirm: `Schedule this mass DM for ${metZoneEnAmsterdam(planMoment, zone)}?`,
              request: plannen,
              onDone: naVerzenden,
              successText: 'Mass DM scheduled.',
            })}
          >
            Schedule
          </button>
          <button
            className="btn primary sm"
            type="button"
            disabled={!kanVersturen}
            onClick={() => run({
              label: 'Mass DM',
              confirm: naarEchteFans
                ? 'Send this mass DM to real fans now?'
                : 'Send this mass DM to the test accounts?',
              request: () => massVerzoek(false),
              onDone: naVerzenden,
              successText: naarEchteFans ? 'Mass DM started.' : 'Test mass DM started.',
            })}
          >
            {naarEchteFans ? 'Send to real fans' : 'Send to test accounts'}
          </button>
        </>
      }
    >
      <MassDmVelden
        w={w}
        zet={zet}
        keuzes={keuzes}
        magBetaald={magBetaald}
        platformFilter={platformFilter}
        onWijzig={wijzig}
      />

      <MassDmTelling preview={preview} />

      <TijdKiezer
        datum={plan.datum} tijd={plan.tijd} zone={zone} naam="Schedule"
        onChange={setPlan}
        label={`Schedule for later (optional, ${zoneLabel(zone)} time)`}
        hint="Used by “Schedule” (starts at now + 15 minutes). “Send” sends right away. A scheduled
              campaign runs on its own and shows up under “My scheduled” in Chats."
      />
      {planVoorbij ? (
        <span className="tag bad" data-testid="plan-tijd-voorbij">{TIJD_VOORBIJ}</span>
      ) : null}

      {layoutOpen ? (
        <MassDmBerichtVoorbeeld w={w} a={a} preview={preview} plek={previewPlek}
                                onSluit={() => setLayoutOpen(false)} />
      ) : null}

      {naarEchteFans ? (
        <Field label="Type SEND to confirm" hint="Only needed for real fans.">
          <input className="inp" value={bevestiging} placeholder="SEND"
                 onChange={(e) => setBevestiging(e.target.value)} />
        </Field>
      ) : null}
    </Modal>
  )
}
